import GroupOutlined from '@mui/icons-material/GroupOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import ThumbUpOutlined from '@mui/icons-material/ThumbUpOutlined';
import { useTheme } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { filterStateDefault as deviceGroupFilterStateDefault } from 'v2/filters/deviceGroups/deviceGroupFilterConstants';
import { serializeBooleanRecordToParam } from 'v2/filters/filterUrlParams';
import { filterAccessDefault as userGroupFilterAccessDefault } from 'v2/filters/userGroups/userGroupFilterConstants';
import { filterAccessDefault as usersFilterAccessDefault } from 'v2/filters/users/userFilterConstants';
import { RowsProps } from 'v2/pages/Dashboard/cards/Card';
import { DEVICE_GROUPS_PATH, DEVICES_OVERVIEW_PATH, USER_GROUPS_OVERVIEW_PATH, USERS_OVERVIEW_PATH } from 'v2/paths';
import { formatCountLabel } from 'v2/utils/formatCountLabel';

interface Props {
  totalUsers: number;
  totalUserGroups: number;
  totalDevices: number;
  totalDeviceGroups: number;
  usersWithoutGroup: number;
  userGroupsWithoutSchedule: number;
  userGroupsWithoutDevices: number;
  userGroupsWithoutUsers: number;
  deviceGroupsWithoutDevices: number;
  deviceGroupsWithoutUserGroups: number;
}

const useTodoRows = ({
  totalUsers,
  totalUserGroups,
  totalDevices,
  totalDeviceGroups,
  usersWithoutGroup,
  userGroupsWithoutSchedule,
  userGroupsWithoutDevices,
  userGroupsWithoutUsers,
  deviceGroupsWithoutDevices,
  deviceGroupsWithoutUserGroups
}: Props): RowsProps[] => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  return useMemo(() => {
    const emptySystemRows = [
      {
        isEmpty: totalUsers === 0,
        text: translation.dashboard_no_users_in_system,
        Icon: PersonOutlined,
        onClick: () => navigate(USERS_OVERVIEW_PATH)
      },
      {
        isEmpty: totalUserGroups === 0,
        text: translation.dashboard_no_user_groups_in_system,
        Icon: GroupOutlined,
        onClick: () => navigate(USER_GROUPS_OVERVIEW_PATH)
      },
      {
        isEmpty: totalDevices === 0,
        text: translation.dashboard_no_devices_in_system,
        Icon: LockOutlined,
        onClick: () => navigate(DEVICES_OVERVIEW_PATH)
      },
      {
        isEmpty: totalDeviceGroups === 0,
        text: translation.dashboard_no_device_groups_in_system,
        Icon: LockOutlined,
        onClick: () => navigate(DEVICE_GROUPS_PATH)
      }
    ].filter((row) => row.isEmpty);

    const issueRows = [
      {
        count: usersWithoutGroup,
        text: formatCountLabel(
          usersWithoutGroup,
          translation.users_without_group_singular,
          translation.users_without_group_plural
        ),
        Icon: PersonOutlined,
        onClick: () => {
          const access = serializeBooleanRecordToParam({ ...usersFilterAccessDefault, noGroup: true });
          navigate(`${USERS_OVERVIEW_PATH}?access=${access}`);
        }
      },
      {
        count: userGroupsWithoutSchedule,
        text: formatCountLabel(
          userGroupsWithoutSchedule,
          translation.user_groups_without_schedule_singular,
          translation.user_groups_without_schedule_plural
        ),
        Icon: GroupOutlined,
        onClick: () => {
          const access = serializeBooleanRecordToParam({ ...userGroupFilterAccessDefault, noSchedule: true });
          navigate(`${USER_GROUPS_OVERVIEW_PATH}?access=${access}`);
        }
      },
      {
        count: userGroupsWithoutDevices,
        text: formatCountLabel(
          userGroupsWithoutDevices,
          translation.user_groups_without_devices_singular,
          translation.user_groups_without_devices_plural
        ),
        Icon: GroupOutlined,
        onClick: () => {
          const access = serializeBooleanRecordToParam({ ...userGroupFilterAccessDefault, noDevices: true });
          navigate(`${USER_GROUPS_OVERVIEW_PATH}?access=${access}`);
        }
      },
      {
        count: userGroupsWithoutUsers,
        text: formatCountLabel(
          userGroupsWithoutUsers,
          translation.user_groups_without_users_singular,
          translation.user_groups_without_users_plural
        ),
        Icon: GroupOutlined,
        onClick: () => {
          const access = serializeBooleanRecordToParam({ ...userGroupFilterAccessDefault, noUsers: true });
          navigate(`${USER_GROUPS_OVERVIEW_PATH}?access=${access}`);
        }
      },
      {
        count: deviceGroupsWithoutDevices,
        text: formatCountLabel(
          deviceGroupsWithoutDevices,
          translation.device_groups_without_devices_singular,
          translation.device_groups_without_devices_plural
        ),
        Icon: LockOutlined,
        onClick: () => {
          const state = serializeBooleanRecordToParam({ ...deviceGroupFilterStateDefault, noDevices: true });
          navigate(`${DEVICE_GROUPS_PATH}?state=${state}`);
        }
      },
      {
        count: deviceGroupsWithoutUserGroups,
        text: formatCountLabel(
          deviceGroupsWithoutUserGroups,
          translation.device_groups_without_user_groups_singular,
          translation.device_groups_without_user_groups_plural
        ),
        Icon: LockOutlined,
        onClick: () => {
          const state = serializeBooleanRecordToParam({ ...deviceGroupFilterStateDefault, noUserGroups: true });
          navigate(`${DEVICE_GROUPS_PATH}?state=${state}`);
        }
      }
    ].filter((row) => row.count > 0);

    const rows = [...emptySystemRows, ...issueRows];

    if (rows.length === 0) {
      return [
        {
          Icon: ThumbUpOutlined,
          text: translation.system_complete,
          onClick: () => navigate(DEVICES_OVERVIEW_PATH),
          color: theme.custom.status.success.selected,
          iconColor: theme.palette.success.main,
          textColor: theme.palette.success.light
        }
      ];
    }

    return rows.map((row) => ({
      Icon: row.Icon,
      text: row.text,
      onClick: row.onClick,
      color: theme.custom.status.error.selected,
      iconColor: theme.palette.error.main,
      textColor: theme.palette.error.light
    }));
  }, [
    totalUsers,
    totalUserGroups,
    totalDevices,
    totalDeviceGroups,
    usersWithoutGroup,
    userGroupsWithoutSchedule,
    userGroupsWithoutDevices,
    userGroupsWithoutUsers,
    deviceGroupsWithoutDevices,
    deviceGroupsWithoutUserGroups,
    translation,
    theme,
    navigate
  ]);
};

export default useTodoRows;
